import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, Users, Building2, History, Bot, MapPin,
  Bell, Shield, Share2, TrendingUp, BarChart3, Star, X, 
  Sparkles, Target, Vote, Newspaper, Settings, CreditCard
} from 'lucide-react';

const navItems = [
  {
    section: 'Home',
    color: 'from-blue-500 to-indigo-600',
    items: [
      { path: '/', icon: Home, label: 'Home' },
      { path: '/politicians', icon: Users, label: 'Politicians' },
      { path: '/map', icon: MapPin, label: 'UK Map' },
      { path: '/trending', icon: TrendingUp, label: 'Trending' },
    ]
  },
  {
    section: 'Elections',
    color: 'from-emerald-500 to-teal-600',
    items: [
      { path: '/odds', icon: Target, label: 'MP Seat Odds' },
      { path: '/council-odds', icon: Building2, label: 'Council Odds' },
      { path: '/election-history', icon: History, label: 'History' },
      { path: '/share', icon: Share2, label: 'Share Card', isNew: true },
    ]
  },
  {
    section: 'Research',
    color: 'from-purple-500 to-pink-600',
    items: [
      { path: '/ai-research', icon: Bot, label: 'AI Research' },
      { path: '/compare', icon: BarChart3, label: 'Compare MPs' },
      { path: '/polls', icon: Vote, label: 'Polls' },
    ]
  },
  {
    section: 'Account',
    color: 'from-amber-500 to-orange-600',
    items: [
      { path: '/watchlist', icon: Star, label: 'Watchlist' },
      { path: '/settings', icon: Bell, label: 'Alerts' },
      { path: '/subscribe', icon: CreditCard, label: 'Subscribe' },
      { path: '/submit-tip', icon: Shield, label: 'Submit Tip' },
    ]
  }
];

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();

  // Close sidebar when route changes
  useEffect(() => {
    onToggle?.(false);
  }, [location.pathname]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={() => onToggle?.(false)}
      />

      {/* Sidebar Panel */}
      <div 
        className="fixed inset-y-0 left-0 w-80 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 z-50 overflow-y-auto shadow-2xl"
        data-testid="mobile-sidebar"
      >
        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <div>
              <div className="font-bold text-white">The Watchtower</div>
              <div className="text-xs text-slate-400">Political Accountability</div>
            </div>
          </div>
          <button
            onClick={() => onToggle?.(false)}
            className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Sections */}
        <nav className="p-4 space-y-6">
          {navItems.map((section) => (
            <div key={section.section}>
              {/* Section Header */}
              <div className={`bg-gradient-to-r ${section.color} rounded-xl p-3 mb-3 shadow-lg`}>
                <span className="text-white font-bold text-sm uppercase tracking-wider">
                  {section.section}
                </span>
              </div>
              
              {/* Section Items */}
              <div className="space-y-1 pl-2">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-white text-slate-900 shadow-lg' 
                          : 'text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${isActive ? 'text-slate-700' : ''}`} />
                      <span className="font-medium">{item.label}</span>
                      {item.isNew && (
                        <span className="ml-auto px-2 py-0.5 bg-gradient-to-r from-pink-500 to-red-500 text-white text-[10px] font-bold rounded-full uppercase">
                          New
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className="font-bold text-purple-300 text-sm">Pro Tip</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Generate shareable cards of your constituency to spread awareness on social media!
            </p>
            <Link 
              to="/share"
              className="mt-3 block text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-bold py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
            >
              Try It Now →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
